// A Generic Mapper for mapping table row and columns
import React from 'react'
import { RowDataWithKey, TableColumn } from '../Component/EnhancedData/SortableTable/Types'
import { InfoIconWithOptionalRequiredSymbol } from '../Component/InfoIconWithOptionalRequiredSymbol'
import { toCSSClassNameWithSuffix } from '../Helper/util'
import { FieldMap } from '../types'

const displayNameWithTooltip = (name: string, hint: string, className: string): React.JSX.Element => {
  return (
    <>
      {name} <InfoIconWithOptionalRequiredSymbol message={hint} onHover={true} className={className}/>
    </>
  )
}

const toTableColumn = <T extends object> (column: TableColumn<T>): TableColumn<T> => {
  const displayName = column?.tooltip !== undefined && typeof column.displayName === 'string'
    ? displayNameWithTooltip(column.displayName, column.tooltip, `hint common-hint ${toCSSClassNameWithSuffix(column.id, 'hint')}`)
    : column.displayName
  return { ...column, displayName }
}

const toTableColumns = <T extends object> (columns: TableColumn<T>[]): TableColumn<T>[] => (
  columns.map(column => toTableColumn(column))
)

const toTableRow = <S extends Object, T extends Object>(
  source: S,
  fieldMapping: FieldMap<S, T>,
  rowKey?: number | string,
  defaultData?: T
): RowDataWithKey<T> => {
  const targetRow = Object.entries(fieldMapping).reduce<T>(
    (acc, [sourceKey, targetKey]) => {
      acc[targetKey as keyof T] = (source[sourceKey as keyof S] as unknown) as T[keyof T]
      return acc
    },
    defaultData || {} as T
  )
  return {
    key: rowKey || Math.random().toString(36).slice(2, 11),
    ...targetRow,
  }
}

const toTableRows = <S extends Object, T extends Object>(
  sources: S[],
  fieldMapping: FieldMap<S, T>,
  uniqueKey?: number | string,
  defaultData?: T
): RowDataWithKey<T>[] => {
  return sources.map(source => toTableRow(source, fieldMapping, uniqueKey, defaultData))
}

export { toTableColumns, toTableRows }
